import React, { useState, useCallback, useEffect } from "react";
import { format, subDays, startOfDay, endOfDay } from "date-fns";
import { useHotel } from "../../contexts/HotelContext";
import { useToast } from "../../contexts/ToastContext";
import Layout from "../../components/Layout";
import { useCallViewsDashboard } from "../../hooks/useCallViewsDashboard";
import { useCallViewsExport } from "../../hooks/useCallViewsExport";
import {
  DateRangePicker,
  ExportButton,
  LoadingSkeleton,
  ErrorState,
  AccessDeniedState,
} from "../../components/admin/llm-usage";
import {
  SummaryCards,
  ChannelActionBreakdown,
  ByDayChart,
  UsersTable,
  TenantsTable,
  InfoBox,
  CallViewsEmptyState,
} from "../../components/admin/call-views";

const getDefaultDateRange = () => {
  const now = new Date();
  const start = startOfDay(subDays(now, 30));
  const end = endOfDay(now);
  return {
    startDate: format(start, "yyyy-MM-dd"),
    endDate: format(end, "yyyy-MM-dd"),
  };
};

const CallViews: React.FC = () => {
  const { user, hasModuleEnabled } = useHotel();
  const { addToast } = useToast();

  const [dateRange, setDateRange] = useState(getDefaultDateRange);

  // Mirror ModeratorRoute: global admin OR kiedynabory moderator module.
  // Tenant-level grants:schedule is enforced by the backend ModuleGuard.
  const canModerate =
    user?.role === "admin" || hasModuleEnabled("admin:kiedynabory:write");

  const { data, isLoading, isRefetching, error, refetch } =
    useCallViewsDashboard({
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
      enabled: canModerate,
    });

  const {
    exportCsv,
    isLoading: isExporting,
    isRateLimited,
    remainingExports,
    countdown,
    isSuccess: isExportSuccess,
    error: exportError,
  } = useCallViewsExport();

  // M21: toast on successful export
  useEffect(() => {
    if (isExportSuccess) {
      addToast({ message: "Eksport gotowy.", type: "success" });
    }
  }, [isExportSuccess, addToast]);

  // M17: toast on export error (e.g. 400 when date range > 90 days)
  useEffect(() => {
    if (exportError) {
      addToast({
        message: "Eksport obejmuje maksymalnie 90 dni. Zawęź zakres dat.",
        type: "error",
      });
    }
  }, [exportError, addToast]);

  const handleDateChange = useCallback((startDate: string, endDate: string) => {
    setDateRange({ startDate, endDate });
  }, []);

  // M18: reset date range to default 30 days for the EmptyState CTA
  const handleResetDateRange = useCallback(() => {
    setDateRange(getDefaultDateRange());
  }, []);

  const handleExport = useCallback(() => {
    exportCsv({
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
    });
  }, [exportCsv, dateRange]);

  if (!canModerate) {
    return (
      <Layout>
        <div className="space-y-6">
          <div>
            <h1 className="text-2xl font-bold text-neutral-900 text-left">
              Statystyki przeglądania naborów
            </h1>
          </div>
          <AccessDeniedState message="Ta strona jest dostępna tylko dla administratorów z dostępem do modułu grants:schedule." />
        </div>
      </Layout>
    );
  }

  const isEmpty = data && data.summary.totalViews === 0;

  return (
    <Layout>
      <div
        className="relative space-y-6"
        data-testid="call-views-page"
        aria-busy={isRefetching}
      >
        {/* Refetch Overlay */}
        {isRefetching && !isLoading && (
          <div
            data-testid="refetch-overlay"
            className="absolute inset-0 bg-white/50 flex items-center justify-center z-10"
            aria-live="polite"
          >
            <span className="sr-only">Aktualizowanie danych</span>
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
          </div>
        )}

        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-neutral-900">
              Statystyki przeglądania naborów
            </h1>
            <p className="text-neutral-600 mt-1">
              Kto i jak często przegląda nabory — w podziale na kanał (web / api /
              email) i organizację
            </p>
          </div>

          <ExportButton
            onClick={handleExport}
            isLoading={isExporting}
            isRateLimited={isRateLimited}
            remainingExports={remainingExports}
            countdown={countdown}
            disabled={isLoading || !!error || isEmpty}
          />
        </div>

        {/* Amber disclaimer — above the fold */}
        <InfoBox />

        {/* Date Range Picker */}
        <div className="bg-white rounded-lg border border-neutral-200 p-4">
          <DateRangePicker
            startDate={dateRange.startDate}
            endDate={dateRange.endDate}
            onDateChange={handleDateChange}
            maxDays={365}
          />
          {data && (
            <div className="mt-2 text-sm text-neutral-500">
              Dane od{" "}
              <span className="font-medium">
                {format(new Date(data.dateRange.startDate), "d MMM yyyy")}
              </span>{" "}
              do{" "}
              <span className="font-medium">
                {format(new Date(data.dateRange.endDate), "d MMM yyyy")}
              </span>
            </div>
          )}
        </div>

        {/* Loading State */}
        {isLoading && <LoadingSkeleton />}

        {/* Error State */}
        {error && <ErrorState error={error} onRetry={() => refetch()} />}

        {/* Empty State */}
        {!isLoading && !error && isEmpty && (
          <CallViewsEmptyState onChangeDateRange={handleResetDateRange} />
        )}

        {/* Dashboard Content */}
        {!isLoading && !error && data && !isEmpty && (
          <>
            <SummaryCards summary={data.summary} />

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <ByDayChart data={data.byDay} />
              <ChannelActionBreakdown
                totalByChannelAction={data.summary.totalByChannelAction}
              />
            </div>

            <UsersTable data={data.byUser} />
            <TenantsTable data={data.byTenant} />
          </>
        )}
      </div>
    </Layout>
  );
};

export default CallViews;
